import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import AddLinkOutlinedIcon from "@mui/icons-material/AddLinkOutlined";
import {
  useCreateQuickPaymentLinkMutation,
  useLazyLookupQuickPaymentCustomerQuery,
  useSendQuickPaymentLinkMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import useDebounce from "../../Components/SearchDebonce/useDebounce";

const BRAND = "#2f3891";

/** Matches the label/input treatment used by the Company and Users forms. */
const fieldLabel = {
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  letterSpacing: 0.4,
  mb: 0.75,
  display: "block",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    bgcolor: "#f8fafc",
    transition: "all 0.2s",
    "&:hover": { bgcolor: "#f1f5f9" },
    "&.Mui-focused": {
      bgcolor: "#ffffff",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: BRAND, borderWidth: 2 },
    },
  },
};

/**
 * The native spinner arrows crowd the currency adornments and invite click-to-
 * increment on a money field. Hidden rather than switching to type="text" so
 * mobile keyboards stay numeric.
 */
const amountSx = {
  ...inputSx,
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
  "& input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
};

const Required = () => <Box component="span" sx={{ color: "#dc2626" }}>&nbsp;*</Box>;

type Created = {
  id: number;
  url: string | null;
  token: string;
  customer_name: string;
  customer_email: string | null;
  reference_number: string;
  amount: number;
  currency: string;
};

const EMPTY = {
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  reference_number: "",
  amount: "",
};

/** Outcome of the phone lookup, for the hint under the field. */
type LookupState = "idle" | "searching" | "found" | "none";

/**
 * Create a Quick Payment Link.
 *
 * A payment request, not an invoice: five fields, no line items, no tax, no
 * due date. Anything needing those belongs in the invoice flow.
 *
 * The dialog has two phases. Phase one captures the details; phase two shows
 * the generated URL and asks — separately — whether to email the customer and
 * whether to notify staff. Both default to off, because copying the link and
 * sending it by SMS or WhatsApp is a normal way to use this.
 */
const CreatePaymentLinkDialog = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [form, setForm] = useState({ ...EMPTY });
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [created, setCreated] = useState<Created | null>(null);
  const [copied, setCopied] = useState(false);
  const [emailCustomer, setEmailCustomer] = useState(false);
  const [emailAdmin, setEmailAdmin] = useState(false);
  const [lookupState, setLookupState] = useState<LookupState>("idle");

  const [createLink, { isLoading: creating }] = useCreateQuickPaymentLinkMutation();
  const [sendLink, { isLoading: sending }] = useSendQuickPaymentLinkMutation();
  const [lookupCustomer, { isFetching: looking }] = useLazyLookupQuickPaymentCustomerQuery();

  // Debounced so the lookup fires once the number stops changing rather than
  // on every keystroke.
  const debouncedPhone = useDebounce(form.customer_phone, 500);

  useEffect(() => {
    const digits = (debouncedPhone ?? "").replace(/\D/g, "");
    if (!open || created || digits.length < 10) {
      if (digits.length < 10) setLookupState("idle");
      return;
    }

    let cancelled = false;
    setLookupState("searching");

    lookupCustomer(debouncedPhone)
      .unwrap()
      .then((res: any) => {
        const match = res?.data;
        if (cancelled) return;

        if (!match) {
          // Not an error: entering someone new is the common case.
          setLookupState("none");
          return;
        }

        setLookupState("found");
        setCustomerId(match.customer_id ?? null);
        // Only fills blanks. Whatever staff typed themselves wins — they may be
        // correcting exactly the stale record this lookup found.
        setForm((f) => ({
          ...f,
          customer_name: f.customer_name || match.customer_name || "",
          customer_email: f.customer_email || match.customer_email || "",
        }));
      })
      .catch(() => {
        /* A lookup is a convenience; never block the form. */
        if (!cancelled) setLookupState("idle");
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedPhone, open, created, lookupCustomer]);

  const amountValue = Number(form.amount);
  const canSubmit =
    form.customer_name.trim() !== "" &&
    form.reference_number.trim() !== "" &&
    Number.isFinite(amountValue) &&
    amountValue > 0;

  const reset = () => {
    setForm({ ...EMPTY });
    setCustomerId(null);
    setCreated(null);
    setCopied(false);
    setEmailCustomer(false);
    setEmailAdmin(false);
    setLookupState("idle");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    try {
      const res: any = await createLink({
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || undefined,
        customer_phone: form.customer_phone.trim() || undefined,
        reference_number: form.reference_number.trim(),
        amount: amountValue,
        customer_id: customerId ?? undefined,
      }).unwrap();

      setCreated(res?.data);
      // Pre-tick the customer email only when there is an address to send to.
      setEmailCustomer(Boolean(res?.data?.customer_email));
      onCreated?.();
    } catch (err: any) {
      // Covers the unconfigured-Payment-URL case, whose message names the
      // exact setting to fix.
      showErrorMessage(err?.data?.message ?? "The payment link could not be created");
    }
  };

  const handleCopy = async () => {
    if (!created?.url) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showErrorMessage("Could not copy automatically — select the link and copy it manually");
    }
  };

  const handleSend = async () => {
    if (!created) return;
    if (!emailCustomer && !emailAdmin) {
      handleClose();
      return;
    }
    try {
      const res: any = await sendLink({
        id: created.id,
        customer: emailCustomer,
        admin: emailAdmin,
      }).unwrap();
      showMessage(res?.message ?? "Payment request sent", "success");
      handleClose();
    } catch (err: any) {
      showErrorMessage(err?.data?.message ?? "The email could not be sent");
    }
  };

  // ── Shared chrome ────────────────────────────────────────────────────────

  const header = (title: string, subtitle: string, icon: React.ReactNode) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.75,
        px: 3,
        pt: 3,
        pb: 2.5,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 2,
          bgcolor: "#eef2ff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.4 }}>{subtitle}</Typography>
      </Box>
      <IconButton size="small" onClick={handleClose} sx={{ mt: -0.5, mr: -0.5, color: "#94a3b8" }}>
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  const footerSx = {
    px: 3,
    py: 2,
    gap: 1,
    borderTop: "1px solid #e2e8f0",
    bgcolor: "#f8fafc",
  };

  const secondaryButtonSx = {
    textTransform: "none",
    borderRadius: 1.5,
    borderColor: "#e2e8f0",
    color: "#64748b",
    px: 2.5,
    "&:hover": { borderColor: "#cbd5e1", bgcolor: "#fff" },
  };

  const primaryButtonSx = {
    textTransform: "none",
    borderRadius: 1.5,
    bgcolor: BRAND,
    fontWeight: 600,
    px: 2.5,
    boxShadow: "none",
    "&:hover": { bgcolor: "#252d75", boxShadow: "none" },
    // Readable rather than a grey blob, so a disabled primary still reads as
    // the action you are working towards.
    "&.Mui-disabled": { bgcolor: "#c7d2fe", color: "#ffffff" },
  };

  const twoColumn = {
    display: "flex",
    gap: 2,
    flexDirection: { xs: "column", sm: "row" },
  };

  // ── Phase 1: the details ─────────────────────────────────────────────────

  const phoneHint = () => {
    if (lookupState === "searching") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75 }}>
          <CircularProgress size={11} sx={{ color: "#94a3b8" }} />
          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Looking up customer…</Typography>
        </Box>
      );
    }

    if (lookupState === "found") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#16a34a" }} />
          <Typography sx={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>Customer found</Typography>
        </Box>
      );
    }

    // "none" reads the same as idle on purpose — a number with no match is
    // normal, and staff simply type the customer in.
    return (
      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75 }}>
        Enter a phone number to find an existing customer.
      </Typography>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: { xs: "100%", sm: 660 },
          maxWidth: "100%",
          m: { xs: 1.5, sm: 4 },
        },
      }}
    >
      {!created ? (
        <>
          {header(
            "Create Payment Link",
            "Create a secure payment request and send the customer a unique link to pay online.",
            <AddLinkOutlinedIcon sx={{ color: BRAND, fontSize: 20 }} />
          )}

          <DialogContent sx={{ px: 3, pt: 0, pb: 3, borderTop: "1px solid #f1f5f9" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2.5 }}>
              <Box sx={twoColumn}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={fieldLabel}>Phone Number</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="(647) 710-0532"
                    value={form.customer_phone}
                    onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                    sx={inputSx}
                    InputProps={{
                      endAdornment: looking ? (
                        <InputAdornment position="end">
                          <CircularProgress size={14} sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                  {phoneHint()}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={fieldLabel}>
                    Customer Name
                    <Required />
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Company or customer name"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    sx={inputSx}
                  />
                </Box>
              </Box>

              <Box>
                <Typography sx={fieldLabel}>Email Address</Typography>
                <TextField
                  size="small"
                  fullWidth
                  type="email"
                  placeholder="name@example.com"
                  value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                  sx={inputSx}
                />
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75 }}>
                  Optional — required only if you want to email the payment request.
                </Typography>
              </Box>

              <Box sx={twoColumn}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={fieldLabel}>
                    Invoice / Reference Number
                    <Required />
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="e.g. VM0001"
                    value={form.reference_number}
                    onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
                    sx={inputSx}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={fieldLabel}>
                    Amount
                    <Required />
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    sx={amountSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>$</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>CAD</Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={footerSx}>
            <Button variant="outlined" onClick={handleClose} sx={secondaryButtonSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canSubmit || creating}
              onClick={handleCreate}
              startIcon={
                creating ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <AddLinkOutlinedIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={primaryButtonSx}
            >
              {creating ? "Creating…" : "Create Payment Link"}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          {header(
            "Payment Link Created",
            "Share this link with the customer, or email it to them below.",
            <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 22 }} />
          )}

          <DialogContent sx={{ px: 3, pt: 0, pb: 3, borderTop: "1px solid #f1f5f9" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2.5 }}>
              {/* Summary — what was requested, at a glance. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                    {created.customer_name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>
                    Reference {created.reference_number}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: BRAND, whiteSpace: "nowrap" }}>
                  ${Number(created.amount).toFixed(2)}{" "}
                  <Box component="span" sx={{ fontSize: 11, color: "#94a3b8" }}>
                    {created.currency}
                  </Box>
                </Typography>
              </Box>

              {/* The link is the point of this screen, so it gets the emphasis. */}
              <Box>
                <Typography sx={fieldLabel}>Payment Link</Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "stretch", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                      px: 1.75,
                      py: 1.25,
                      borderRadius: 1.5,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: "monospace",
                        color: created.url ? "#0f172a" : "#dc2626",
                        wordBreak: "break-all",
                      }}
                    >
                      {created.url ?? "Payment URL is not configured under Settings → URL"}
                    </Typography>
                  </Box>
                  {created.url && (
                    <Button
                      variant={copied ? "outlined" : "contained"}
                      onClick={handleCopy}
                      startIcon={
                        copied ? (
                          <CheckCircleOutlineIcon sx={{ fontSize: 17 }} />
                        ) : (
                          <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
                        )
                      }
                      sx={
                        copied
                          ? {
                              textTransform: "none",
                              borderRadius: 1.5,
                              borderColor: "#bbf7d0",
                              color: "#16a34a",
                              bgcolor: "#f0fdf4",
                              minWidth: 116,
                              "&:hover": { borderColor: "#bbf7d0", bgcolor: "#f0fdf4" },
                            }
                          : { ...primaryButtonSx, minWidth: 116 }
                      }
                    >
                      {copied ? "Copied" : "Copy Link"}
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Two independent choices — staff often send the link themselves. */}
              <Box>
                <Typography sx={{ ...fieldLabel, mb: 1 }}>Send payment request</Typography>

                <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                        Email this request to the customer
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>
                        {created.customer_email || "No email address on this request"}
                      </Typography>
                    </Box>
                    <Switch
                      checked={emailCustomer}
                      disabled={!created.customer_email}
                      onChange={(e) => setEmailCustomer(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                        Send a copy to admin
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>
                        Goes to users with “Send Payment Emails” enabled
                      </Typography>
                    </Box>
                    <Switch
                      checked={emailAdmin}
                      onChange={(e) => setEmailAdmin(e.target.checked)}
                      size="small"
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: BRAND },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={footerSx}>
            <Button variant="outlined" onClick={handleClose} sx={secondaryButtonSx}>
              Done
            </Button>
            <Tooltip title={!emailCustomer && !emailAdmin ? "Turn on a recipient above to send" : ""} arrow>
              <span>
                <Button
                  variant="contained"
                  disabled={sending || (!emailCustomer && !emailAdmin)}
                  onClick={handleSend}
                  startIcon={
                    sending ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <ForwardToInboxOutlinedIcon sx={{ fontSize: 17 }} />
                    )
                  }
                  sx={primaryButtonSx}
                >
                  {sending ? "Sending…" : "Send Email"}
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CreatePaymentLinkDialog;
